import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdDeleteForever, MdSkipNext, MdSkipPrevious } from 'react-icons/md';
import { SuperHeader as PresetSuperHeader } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { HeaderInfo } from '@src/utils/columnMetricSystem/configUtils';

interface SuperHeaderProps {
  colCount: number;
  columnI: number;
  info: Record<string, HeaderInfo | true | undefined>;
  onSuperHeaderChange: (newSuperHeaders: PresetSuperHeader[]) => void;
  superHeaders: PresetSuperHeader[];
}

export const SuperHeader = ({
  colCount,
  columnI,
  info,
  onSuperHeaderChange,
  superHeaders
}: SuperHeaderProps) => {
  const ourInfo = info[columnI];
  const ourHeaderInfo = ourInfo && typeof ourInfo !== 'boolean' ? ourInfo : undefined;

  const onUpdateText = useCallback(
    (text: string) => {
      if (!ourHeaderInfo) return;
      const newSuperHeaders = [...superHeaders];
      newSuperHeaders[ourHeaderInfo.superHeaderIndex] = {
        ...newSuperHeaders[ourHeaderInfo.superHeaderIndex],
        data: { ...newSuperHeaders[ourHeaderInfo.superHeaderIndex].data, text }
      };
      onSuperHeaderChange(newSuperHeaders);
    },
    [onSuperHeaderChange, ourHeaderInfo, superHeaders]
  );

  const onDelete = useCallback(() => onUpdateText(''), [onUpdateText]);

  const onResize = useCallback(
    (grow: boolean, side: 'end' | 'start') => {
      if (!ourHeaderInfo) return;
      const newSuperHeaders = [...superHeaders];
      const { superHeaderIndex } = ourHeaderInfo;
      newSuperHeaders[superHeaderIndex] = {
        ...newSuperHeaders[superHeaderIndex],
        span: newSuperHeaders[superHeaderIndex].span + (grow ? 1 : -1)
      };
      if (side === 'start' && newSuperHeaders[superHeaderIndex - 1]) {
        newSuperHeaders[superHeaderIndex - 1] = {
          ...newSuperHeaders[superHeaderIndex - 1],
          span: newSuperHeaders[superHeaderIndex - 1].span + (grow ? -1 : 1)
        };
      } else if (side === 'end' && newSuperHeaders[superHeaderIndex + 1]) {
        newSuperHeaders[superHeaderIndex + 1] = {
          ...newSuperHeaders[superHeaderIndex + 1],
          span: newSuperHeaders[superHeaderIndex + 1].span + (grow ? -1 : 1)
        };
      }
      onSuperHeaderChange(newSuperHeaders);
    },
    [onSuperHeaderChange, ourHeaderInfo, superHeaders]
  );

  const { canLeft, canRight, size } = useMemo(() => {
    if (!ourHeaderInfo) return { canLeft: false, canRight: false, size: 0 };
    const lastSuperHeader = superHeaders[ourHeaderInfo.superHeaderIndex - 1];
    const superHeader = superHeaders[ourHeaderInfo.superHeaderIndex];
    const nextSuperHeader = superHeaders[ourHeaderInfo.superHeaderIndex + 1];
    return {
      canLeft: ourHeaderInfo.start > 0 && !lastSuperHeader?.data.text,
      canRight: ourHeaderInfo.end < colCount - 1 && !nextSuperHeader?.data.text,
      size: superHeader.span
    };
  }, [ourHeaderInfo, superHeaders, colCount]);

  const [localText, setLocalText] = useState(ourHeaderInfo?.text ?? '');

  useEffect(() => {
    setLocalText(ourHeaderInfo?.text ?? '');
  }, [ourHeaderInfo]);

  if (!ourHeaderInfo) return null;

  return (
    <div className='super-header-container'>
      <div style={{ width: `${ourHeaderInfo.width}px` }}>
        <div className='sizer-box'>
          {canLeft && (
            <IconButton
              appearance={Appearance.OUTLINED}
              onClick={() => onResize(true, 'start')}
              size={Size.MD}
            >
              <MdSkipPrevious />
            </IconButton>
          )}
          {size > 1 && (
            <IconButton
              appearance={Appearance.OUTLINED}
              onClick={() => onResize(false, 'start')}
              size={Size.MD}
            >
              <MdSkipNext />
            </IconButton>
          )}
        </div>
        <Form.Control
          onBlur={() => onUpdateText(localText)}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setLocalText(e.currentTarget.value)}
          size='sm'
          value={localText}
        />
        <IconButton
          appearance={Appearance.OUTLINED}
          design={Intent.ERROR}
          onClick={onDelete}
          size={Size.MD}
        >
          <MdDeleteForever />
        </IconButton>
        <div className='sizer-box'>
          {size > 1 && (
            <IconButton
              appearance={Appearance.OUTLINED}
              onClick={() => onResize(false, 'end')}
              size={Size.MD}
            >
              <MdSkipPrevious />
            </IconButton>
          )}
          {canRight && (
            <IconButton
              appearance={Appearance.OUTLINED}
              onClick={() => onResize(true, 'end')}
              size={Size.MD}
            >
              <MdSkipNext />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};
